import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Modal } from "@/components/common";

const show = (v) => (v == null || v === "" ? "—" : String(v));

function MergeDialog({ contactId, other, onClose, onDone }) {
  const [prev, setPrev] = useState(null);
  const [res, setRes] = useState({});
  useState(() => { api.get(`/contacts/${contactId}/merge-preview/${other.id}`).then((r) => setPrev(r.data)).catch((e) => toast.error(errMsg(e))); });
  const merge = async () => {
    try {
      await api.post(`/contacts/${contactId}/merge`, { secondary_id: other.id, resolutions: res });
      toast.success("Contacts merged — images, notes and history kept");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const ready = prev && prev.conflicts.every((c) => res[c.field]);
  return (
    <Modal open onClose={onClose} title="Merge with review" wide testid="merge-dialog">
      {!prev ? <p>Loading…</p> : (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">Emails, phones, tags, source images, notes, emails and history from both contacts are combined. For conflicting details, choose which value to keep — nothing is overwritten silently.</p>
          {prev.conflicts.length === 0 && <p className="rounded-xl bg-emerald-50 p-3 text-sm text-emerald-700">No conflicting fields.</p>}
          {prev.conflicts.map((c) => (
            <div key={c.field} className="rounded-xl border border-slate-200 p-3" data-testid={`merge-conflict-${c.field}`}>
              <p className="cd-label">{c.field.replace(/_/g, " ")}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {["primary", "secondary"].map((side) => (
                  <label key={side} className={`flex cursor-pointer items-start gap-2 rounded-lg border p-2 text-sm ${res[c.field] === side ? "border-teal-600 bg-teal-50" : "border-slate-200"}`}>
                    <input type="radio" data-testid={`merge-choose-${c.field}-${side}`} name={c.field} checked={res[c.field] === side} onChange={() => setRes({ ...res, [c.field]: side })} />
                    <span>{show(c[side])}<span className="block text-xs text-slate-400">{side === "primary" ? "This contact" : "Other contact"}</span></span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <button data-testid="merge-confirm-btn" disabled={!ready} onClick={merge} className="cd-btn-primary w-full">Merge contacts</button>
        </div>
      )}
    </Modal>
  );
}

export default function DuplicatePanel({ contact, reload }) {
  const [merging, setMerging] = useState(null);
  if (!contact.duplicate_candidates?.length) return null;
  const keep = async (other) => {
    try { await api.post(`/contacts/${contact.id}/keep-separate`, { other_id: other.id }); toast.success("Kept separate"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4" data-testid="duplicate-panel">
      <p className="mb-2 flex items-center gap-2 font-bold text-amber-900"><Copy className="h-4 w-4" />Possible duplicate</p>
      {contact.duplicate_candidates.map((d) => (
        <div key={d.id} className="mb-2 rounded-xl bg-white p-3">
          <p className="font-semibold">{d.full_name || d.company || "Contact"} <span className="text-sm font-normal text-slate-500">{d.company && d.full_name ? `· ${d.company}` : ""}</span></p>
          <p className="mb-2 text-xs text-amber-800">Match: {d.reasons.join(", ")}</p>
          <div className="flex flex-wrap gap-2">
            <Link to={`/contacts/${d.id}`} data-testid={`dup-open-${d.id}`} className="cd-btn-secondary h-9 px-3">Open existing</Link>
            <button data-testid={`dup-merge-${d.id}`} onClick={() => setMerging(d)} className="cd-btn-primary h-9 px-3">Merge with review</button>
            <button data-testid={`dup-keep-${d.id}`} onClick={() => keep(d)} className="cd-btn-ghost h-9 px-3">Keep separate</button>
          </div>
        </div>
      ))}
      {merging && <MergeDialog contactId={contact.id} other={merging} onClose={() => setMerging(null)} onDone={() => { setMerging(null); reload(); }} />}
    </div>
  );
}
